import { callApiFilterDefinitions } from "@/app/[tenant]/(console)/comms/call/call-filter-definitions";

import { getTimezone } from "@/lib/timezone";

import {
  CallFetchParams,
  CallLocateResponse,
  CallSummary,
  CallTable,
  CallTranscript,
  CallTransferRequest,
  CallTransferResponse,
  MakeAICall,
  RingGroupAnsweredResponse,
  RingingCallResponse,
  TagSummary,
  TransferTargetsResponse,
} from "@/types/call";
import { Page } from "@/types/pagination";

import { kyInstance } from "@/api/api-config";
import { requestData } from "@/api/api-config";
import { serializeFilterQuerySegments } from "@/features/table-filters/contract";

function serializeCallSearchParams(params: CallFetchParams): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: callApiFilterDefinitions, values: params },
  ]);
}

export async function fetchCalls(
  params: CallFetchParams,
  signal?: AbortSignal,
): Promise<Page<CallTable> | null> {
  const tz = getTimezone();
  return await requestData<Page<CallTable>>("comms/call/list", "get", {
    searchParams: serializeCallSearchParams({ ...params, tz }),
    signal,
  });
}

/** Ask the backend which page of `fetchCalls` contains `callId` under the
 * supplied filters. Returns null if the call isn't in the filtered result set,
 * so the caller can fall back to a "couldn't find linked call" toast. */
export async function locateCall(
  callId: string,
  params: CallFetchParams,
): Promise<CallLocateResponse | null> {
  const tz = getTimezone();
  const searchParams = serializeCallSearchParams({ ...params, tz });
  searchParams.set("call_id", callId);
  return await requestData<CallLocateResponse>("comms/call/locate", "get", {
    searchParams,
  });
}

export async function fetchCallTranscript(
  callId: string,
  signal?: AbortSignal,
): Promise<CallTranscript | null> {
  return await requestData<CallTranscript>(
    `comms/call/${callId}/transcript`,
    "get",
    { signal },
  );
}

export async function fetchTags(): Promise<TagSummary[] | null> {
  return await requestData<TagSummary[]>("comms/call/tags", "get");
}

export async function makeAICall(
  call: MakeAICall,
): Promise<CallSummary | null> {
  return await requestData<CallSummary>("comms/call/ai", "post", {
    json: call,
  });
}

export async function notifyRingGroupAnswered(): Promise<RingGroupAnsweredResponse | null> {
  return await requestData<RingGroupAnsweredResponse>(
    "comms/call/ring-group/answered",
    "post",
  );
}

/** Best-effort: a failure just means the incoming-call banner shows no location. */
export async function fetchRingingCall(): Promise<RingingCallResponse | null> {
  return await requestData<RingingCallResponse>(
    "comms/call/ring-group/ringing",
    "get",
  );
}

export async function fetchTransferTargets(): Promise<TransferTargetsResponse | null> {
  return await requestData<TransferTargetsResponse>(
    "comms/call/transfer-targets",
    "get",
  );
}

export const callQueryKeys = {
  all: ["comms", "call"] as const,
  lists: () => [...callQueryKeys.all, "list"] as const,
  list: (params: CallFetchParams) =>
    [...callQueryKeys.lists(), params] as const,
  transferTargets: () => [...callQueryKeys.all, "transfer-targets"] as const,
};

export const callQueryOptions = {
  transferTargets: () => ({
    queryKey: callQueryKeys.transferTargets(),
    // requestData swallows network errors into a null payload; throw here so
    // React Query treats it as an error (retries, never caches the failure
    // under staleTime) instead of a successful null that would stick for 5m.
    queryFn: async () => {
      const data = await fetchTransferTargets();
      if (!data) {
        throw new Error("Failed to load transfer targets");
      }
      return data;
    },
    // Targets (ring groups + staff) change rarely; a 5-minute cache is
    // plenty and avoids refetching on every transfer dialog open.
    staleTime: 5 * 60 * 1000,
  }),
};

export async function transferCall(
  request: CallTransferRequest,
): Promise<CallTransferResponse | null> {
  return await requestData<CallTransferResponse>(
    "comms/call/transfer",
    "post",
    {
      json: request,
    },
  );
}

export async function getAudioURL(pathToAudio: string): Promise<string | null> {
  try {
    const response = await kyInstance.post("comms/call/stream-audio", {
      json: { path_to_audio: pathToAudio },
      headers: { Accept: "audio/*" },
      timeout: 30000,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch audio");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    return url;
  } catch (error) {
    console.error("Failed to stream audio:", error);
    return null;
  }
}
